import React, { useEffect } from 'react';
import SEOHead from '@/components/seo/SEOHead';
import Features from '@/components/landing/Features';
import { Footer } from '@/components/layout/Footer';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const featuresSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "name": "Meduler Features — Medication Reminder App & Adherence Tracker",
      "description": "Explore Meduler's features: smart medication reminders, adherence tracking, caregiver medication management, offline access, and a privacy-first design.",
      "url": "https://www.meduler.com/features",
      "mainEntity": {
        "@type": "SoftwareApplication",
        "name": "Meduler",
        "applicationCategory": "HealthApplication",
        "operatingSystem": "Web, iOS, Android",
        "featureList": "Medication reminders, Adherence tracking, Caregiver medication management, Offline access, Calendar view, Multiple medication profiles",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD"
        }
      }
    },
    {
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What features does Meduler include for free?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The free Starter plan includes a full medication schedule, dual user profiles for caregivers, offline access, a calendar view, and basic reminder notifications — all without requiring an account or sign-up."
          }
        },
        {
          "@type": "Question",
          "name": "Does Meduler send medication reminder notifications?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. Meduler sends push notifications at your scheduled medication times. You can customize the reminder tone, timing, and snooze interval for each medication."
          }
        },
        {
          "@type": "Question",
          "name": "How does Meduler track medication adherence?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "When you mark a dose as taken, Meduler logs the time and calculates your adherence percentage. Weekly and monthly charts show your consistency at a glance, which you can share with your healthcare provider."
          }
        },
        {
          "@type": "Question",
          "name": "Does Meduler work offline without an internet connection?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. Meduler is offline-first. Your entire medication schedule, reminder history, and adherence data are stored locally on your device and work without Wi-Fi or mobile data."
          }
        },
        {
          "@type": "Question",
          "name": "Can Meduler handle complex medication schedules with multiple drugs?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. Meduler supports unlimited medications with flexible scheduling patterns — daily, twice-daily, weekly, every-N-days, or custom time-based intervals — making it suitable for managing multiple chronic conditions."
          }
        }
      ]
    }
  ]
};

const FeaturesPage: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  useEffect(() => {
    if (typeof window !== 'undefined' && 'gtag' in window) {
      (window as any).gtag('event', 'page_view', {
        page_title: 'Meduler Features',
        page_path: window.location.pathname,
      });
    }

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.update().catch(() => {});
      });
    }
  }, []);

  return (
    <>
      <SEOHead
        title="Features — Medication Reminder App & Adherence Tracker"
        description="Explore Meduler's features: smart medication reminders, adherence tracking, caregiver support, offline access, and a privacy-first design. Free to start."
        keywords="medication reminder app, medication adherence tracker, caregiver medication management, medication schedule app offline, privacy medication tracker"
        canonicalUrl="https://www.meduler.com/features"
        image="https://www.meduler.com/social/og-image.png"
        structuredData={featuresSchema}
      />

      <main className="min-h-screen bg-gradient-to-b from-white via-purple-50 to-white">
        {/* Hero Section — GEO template: question-format H1 + direct-answer paragraph */}
        <section className="pt-4 md:pt-6 pb-12 px-4 md:px-8">
          <motion.div 
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-block px-3 py-1 mb-4 rounded-full bg-purple-100 border border-purple-200">
              <p className="text-sm font-medium text-purple-700">Smart Medication Management</p>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              What Features Does Meduler Include?
            </h1>
            {/* Direct-answer paragraph — within first 100 words */}
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-4">
              Meduler is a free medication reminder app and adherence tracker. Key features include customizable push reminders, a daily schedule view, adherence charts, dual caregiver profiles, and fully offline operation — all without requiring an account or sharing your health data.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
              <a 
                href="/" 
                className="inline-flex items-center justify-center px-6 py-3 bg-purple-600 text-white font-medium rounded-lg shadow-sm hover:bg-purple-700 transition-colors"
              >
                Start for Free
              </a>
              <a 
                href="/how-it-works" 
                className="inline-flex items-center justify-center px-6 py-3 bg-white border border-purple-200 text-purple-600 font-medium rounded-lg shadow-sm hover:bg-purple-50 transition-colors"
              >
                How It Works
              </a>
            </div>
          </motion.div>
        </section>

        {/* The Meduler Adherence Feature Set — named framework */}
        <section className="py-12 px-4 md:px-8 bg-white" aria-labelledby="feature-framework-heading">
          <div className="max-w-4xl mx-auto">
            <h2 id="feature-framework-heading" className="text-2xl font-bold text-center text-gray-900 mb-3">
              The Meduler Adherence Feature Set
            </h2>
            <p className="text-center text-gray-600 mb-10 max-w-2xl mx-auto">
              Five core capabilities that work together to build consistent medication habits.
            </p>
            <div className="grid md:grid-cols-2 gap-5">
              {[
                { num: "01", title: "Smart reminders", desc: "Customizable push notifications at your scheduled times. Set the tone, snooze duration, and reminder window for each medication independently." },
                { num: "02", title: "Adherence tracking", desc: "Mark doses as taken and see your adherence percentage update in real time. Weekly and monthly charts highlight trends and streaks." },
                { num: "03", title: "Caregiver medication management", desc: "Dual user profiles let caregivers manage a loved one's schedule alongside their own, all from one app — no extra account needed." },
                { num: "04", title: "Offline-first operation", desc: "All data is stored locally on your device. The full schedule, history, and reminders work without Wi-Fi or mobile data." },
                { num: "05", title: "Privacy-first design", desc: "Meduler does not sell your health data. Your medication information stays on your device and is never shared with advertisers or third parties." },
              ].map(({ num, title, desc }) => (
                <div key={num} className="flex gap-4 p-5 rounded-xl bg-purple-50 border border-purple-100">
                  <div className="flex-shrink-0 text-2xl font-bold text-purple-300">{num}</div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Segment-specific advice */}
        <section className="py-12 px-4 md:px-8" aria-labelledby="segment-features-heading">
          <div className="max-w-4xl mx-auto">
            <h2 id="segment-features-heading" className="text-2xl font-bold text-center text-gray-900 mb-8">
              Who Benefits Most from Meduler?
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-6 rounded-xl bg-white border border-purple-100 shadow-sm">
                <h3 className="font-bold text-gray-900 mb-3">Patients managing chronic conditions</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Research published in the Annals of Internal Medicine estimates that medication non-adherence costs the US healthcare system up to $289 billion annually. For patients with diabetes, hypertension, or heart disease, Meduler's reminders and adherence charts make it easier to stay consistent with complex multi-drug regimens.
                </p>
              </div>
              <div className="p-6 rounded-xl bg-white border border-purple-100 shadow-sm">
                <h3 className="font-bold text-gray-900 mb-3">Caregivers of elderly family members</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Managing medications for a parent or spouse is one of the most common — and stressful — caregiving tasks. Meduler's dual profile system and caregiver alerts (Plus) reduce the mental load and give caregivers actionable visibility without needing to be physically present.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Limitations section */}
        <section className="py-10 px-4 md:px-8 bg-gray-50 border-y border-gray-100" aria-labelledby="limitations-features-heading">
          <div className="max-w-3xl mx-auto">
            <h2 id="limitations-features-heading" className="text-xl font-bold text-gray-800 mb-4">When Meduler isn't the right fit</h2>
            <ul className="space-y-2 text-gray-600 text-sm list-disc list-inside">
              <li>Meduler does not check for drug-drug or drug-food interactions — always consult your pharmacist.</li>
              <li>Meduler is not an electronic health record (EHR) and does not connect to clinical systems.</li>
              <li>If you require prescription management or refill ordering, a full-service pharmacy app may be more appropriate.</li>
            </ul>
          </div>
        </section>

        {/* Features Component with Animation */}
        <motion.div 
          ref={ref}
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="w-full"
        >
          <Features />
        </motion.div>

        {/* FAQ Section */}
        <section className="py-12 px-4 md:px-8" aria-labelledby="faq-features-heading">
          <div className="max-w-3xl mx-auto">
            <h2 id="faq-features-heading" className="text-2xl font-bold text-center text-gray-900 mb-8">
              Features FAQ
            </h2>
            <div className="space-y-4">
              {[
                { q: "What features does Meduler include for free?", a: "The free Starter plan includes a full medication schedule, dual caregiver profiles, offline access, a calendar view, and push reminders — no account or sign-up required." },
                { q: "Does Meduler send medication reminder notifications?", a: "Yes. Meduler sends push notifications at your scheduled times. Customize the tone, timing, and snooze interval for each medication." },
                { q: "How does Meduler track medication adherence?", a: "Marking a dose as taken logs the exact time and updates your running adherence percentage. Weekly and monthly charts show your consistency over time." },
                { q: "Does Meduler work offline without an internet connection?", a: "Yes. Meduler is offline-first — your schedule, reminders, and history all work without Wi-Fi or mobile data." },
                { q: "Can Meduler handle complex medication schedules?", a: "Yes. Meduler supports unlimited medications with daily, twice-daily, weekly, every-N-days, and custom time patterns." },
              ].map((faq, i) => (
                <details key={i} className="rounded-xl border border-purple-100 bg-white shadow-sm group">
                  <summary className="p-5 font-semibold text-gray-800 cursor-pointer list-none flex justify-between items-center">
                    {faq.q}
                    <span className="text-purple-400 group-open:rotate-180 transition-transform">▼</span>
                  </summary>
                  <div className="px-5 pb-5 text-gray-600 text-sm leading-relaxed">{faq.a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 md:px-8 bg-gradient-to-r from-purple-500 to-indigo-600 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to simplify your medication routine?</h2>
            <p className="text-xl mb-8 text-purple-100">
              Join thousands of users who manage their medications effortlessly with Meduler.
            </p>
            <a 
              href="/" 
              className="inline-flex items-center justify-center px-8 py-3 bg-white text-purple-600 font-medium rounded-lg shadow-lg hover:bg-purple-50 transition-colors"
            >
              Try Meduler Now
            </a>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
};

export default FeaturesPage;
